import React, { useMemo, useState } from 'react';
import { Archive, RefreshCcw, Search } from 'lucide-react';
import { useInvoices } from './hooks/useInvoices';
import { Category, Level } from './types/invoices';
import Breadcrumbs from './components/Breadcrumbs';
import FolderCard from './components/FolderCard';
import FileTable from './components/FileTable';
import { monthLabel } from './utils/invoices';
import { downloadInvoicesZip } from './utils/zipInvoices';

const InvoicesPage: React.FC = () => {
  const { files, groups, loading, error, fetchInvoices, countByCategory } =
    useInvoices();

  const [level, setLevel] = useState<Level>('root');
  const [category, setCategory] = useState<Category | null>(null);
  const [year, setYear] = useState<string | null>(null);
  const [month, setMonth] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [zipping, setZipping] = useState(false); // état pour le bouton

  const goRoot = () => {
    setLevel('root');
    setCategory(null);
    setYear(null);
    setMonth(null);
    setQuery('');
  };
  const goCategory = (c: Category) => {
    setCategory(c);
    setLevel('category');
    setYear(null);
    setMonth(null);
    setQuery('');
  };
  const goYear = (y: string) => {
    setYear(y);
    setLevel('year');
    setMonth(null);
    setQuery('');
  };
  const goMonth = (m: string) => {
    setMonth(m);
    setLevel('month');
  };

  const yearsForCategory = useMemo(() => {
    if (!category) return [];
    const obj = groups[category];
    return Object.keys(obj)
      .filter((y) => y !== 'unknown')
      .sort((a, b) => Number(b) - Number(a))
      .map((y) => ({
        year: y,
        count: Object.values(obj[y]).reduce((s, arr) => s + arr.length, 0),
      }));
  }, [groups, category]);

  const monthsForYear = useMemo(() => {
    if (!category || !year) return [];
    const obj = groups[category][year] ?? {};
    return Object.keys(obj)
      .filter((m) => m !== 'unknown')
      .sort((a, b) => Number(b) - Number(a))
      .map((m) => ({ month: m, count: obj[m].length }));
  }, [groups, category, year]);

  const filesForMonth = useMemo(() => {
    if (!category || !year || !month) return [];
    const arr = groups[category][year]?.[month] ?? [];
    if (!query.trim()) return arr;
    const q = query.toLowerCase();
    return arr.filter((f) => f.name.toLowerCase().includes(q));
  }, [groups, category, year, month, query]);

  const handleDownloadFolder = async () => {
    try {
      setZipping(true);
      if (level === 'root') {
        await downloadInvoicesZip(files, { level: 'root' });
      } else if (level === 'category' && category) {
        await downloadInvoicesZip(files, { level: 'category', category });
      } else if (level === 'year' && category && year) {
        await downloadInvoicesZip(files, { level: 'year', category, year });
      } else if (level === 'month' && category && year && month) {
        await downloadInvoicesZip(files, {
          level: 'month',
          category,
          year,
          month,
        });
      }
    } catch (e: any) {
      alert(e?.message ?? 'Impossible de générer le zip.');
    } finally {
      setZipping(false);
    }
  };

  return (
    <div className="h-full flex flex-col gap-4 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Factures</h1>
          <p className="text-neutral-600">
            Explorateur virtuel par catégories / année / mois
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchInvoices}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-neutral-200 px-3 py-2 hover:bg-neutral-50 disabled:opacity-50"
            aria-label="Rafraîchir"
          >
            <RefreshCcw
              className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
            />
            <span>Rafraîchir</span>
          </button>

          {/* Bouton Télécharger le dossier - toujours visible */}
          <button
            onClick={handleDownloadFolder}
            disabled={zipping || loading}
            className="inline-flex items-center gap-2 rounded-xl border border-neutral-200 px-3 py-2 hover:bg-neutral-50 disabled:opacity-50"
            aria-label="Télécharger le dossier"
            title="Télécharger le dossier (zip)"
          >
            <Archive className={`w-4 h-4 ${zipping ? 'animate-pulse' : ''}`} />
            <span>{zipping ? 'Préparation…' : 'Télécharger le dossier'}</span>
          </button>
        </div>
      </div>

      <Breadcrumbs
        category={category}
        year={year}
        month={month}
        onRoot={goRoot}
        onCategory={(c) => goCategory(c)}
        onYear={(y) => goYear(y)}
      />

      {loading && <div className="p-6 text-neutral-600">Chargement…</div>}
      {error && !loading && (
        <div className="p-6 text-red-600">Erreur : {error}</div>
      )}

      {!loading && !error && (
        <>
          {level === 'root' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FolderCard
                label="Gifts"
                sublabel={`${countByCategory.gifts} fichier${countByCategory.gifts > 1 ? 's' : ''}`}
                onClick={() => goCategory('gifts')}
              />
              <FolderCard
                label="Workshops"
                sublabel={`${countByCategory.workshops} fichier${countByCategory.workshops > 1 ? 's' : ''}`}
                onClick={() => goCategory('workshops')}
              />
            </div>
          )}

          {level === 'category' && category && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {yearsForCategory.length === 0 ? (
                <div className="col-span-full p-6 text-neutral-600">
                  Aucune facture datée.
                </div>
              ) : (
                yearsForCategory.map(({ year, count }) => (
                  <FolderCard
                    key={year}
                    label={year}
                    sublabel={`${count} fichier${count > 1 ? 's' : ''}`}
                    onClick={() => goYear(year)}
                  />
                ))
              )}
            </div>
          )}

          {level === 'year' && category && year && (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              {monthsForYear.length === 0 ? (
                <div className="col-span-full p-6 text-neutral-600">
                  Aucun mois trouvé.
                </div>
              ) : (
                monthsForYear.map(({ month, count }) => (
                  <FolderCard
                    key={month}
                    label={monthLabel(month)} // <---- affichera "09 - Septembre"
                    sublabel={`${count} fichier${count > 1 ? 's' : ''}`}
                    onClick={() => goMonth(month)}
                  />
                ))
              )}
            </div>
          )}

          {level === 'month' && category && year && month && (
            <>
              <div className="relative w-full sm:w-96">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher un fichier…"
                  className="w-full rounded-xl border border-neutral-200 pl-9 pr-3 py-2 outline-none focus:ring-2 focus:ring-neutral-300"
                />
              </div>

              <div className="rounded-2xl border border-neutral-200 overflow-hidden bg-white mt-2">
                <div className="grid grid-cols-12 px-4 py-3 text-xs font-medium text-neutral-600 border-b border-neutral-200">
                  <div className="col-span-6 sm:col-span-7">Fichier</div>
                  <div className="hidden sm:block sm:col-span-3">Date</div>
                  <div className="hidden sm:block sm:col-span-1">Taille</div>
                  <div className="col-span-6 sm:col-span-1 text-right">
                    Actions
                  </div>
                </div>
                <FileTable files={filesForMonth} />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default InvoicesPage;
